import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Bell, Package, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAlerts = ({ token, clients }) => {
  const [alerts, setAlerts] = useState([]);
  const [alertSortBy, setAlertSortBy] = useState('date');
  const [alertSortOrder, setAlertSortOrder] = useState('desc');
  const [alertGroupByClient, setAlertGroupByClient] = useState(false);

  // Receive modal
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [receiveForm, setReceiveForm] = useState({
    package_number: '', carrier: '', weight: '', length: '', width: '', height: '',
    pieces: 1, declared_value: '', notes: '', location: 'miami_warehouse'
  });
  const [receivePhotos, setReceivePhotos] = useState({
    photo_package: null, photo_label: null, photo_package_preview: null, photo_label_preview: null
  });
  const [receiveLoading, setReceiveLoading] = useState(false);

  // Edit alert modal
  const [showEditAlertModal, setShowEditAlertModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [editAlertForm, setEditAlertForm] = useState({
    tracking_number: '', description: '', store_origin: '', estimated_weight: '', declared_value: '', notes: ''
  });

  const axiosConfig = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/alerts`, axiosConfig);
      setAlerts(res.data);
    } catch (error) {
    }
  }, [axiosConfig]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.full_name : 'Desconocido';
  };

  // Sorting & Grouping
  const getSortedAlerts = (alertList) => {
    const sorted = [...alertList];
    if (alertSortBy === 'client') {
      sorted.sort((a, b) => {
        const nameA = getClientName(a.client_id).toLowerCase();
        const nameB = getClientName(b.client_id).toLowerCase();
        return alertSortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      });
    } else {
      sorted.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return alertSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }
    return sorted;
  };

  const getGroupedAlerts = (alertList) => {
    const grouped = {};
    alertList.forEach(alert => {
      const clientName = getClientName(alert.client_id);
      if (!grouped[clientName]) grouped[clientName] = [];
      grouped[clientName].push(alert);
    });
    Object.keys(grouped).forEach(name => {
      grouped[name].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });
    return grouped;
  };

  // Receive modal
  const openReceiveModal = (alert) => {
    setSelectedAlert(alert);
    const tracking = alert.tracking_number?.toUpperCase() || '';
    let detectedCarrier = 'Otro';
    if (tracking.match(/^TBA/)) detectedCarrier = 'Amazon';
    else if (tracking.match(/^1Z/)) detectedCarrier = 'UPS';
    else if (tracking.match(/^[0-9]{12,22}$/)) detectedCarrier = 'FedEx';
    else if (tracking.match(/^94/)) detectedCarrier = 'USPS';
    else if (tracking.match(/^[0-9]{10}$/)) detectedCarrier = 'DHL';

    setReceiveForm({
      package_number: '', carrier: detectedCarrier, weight: alert.estimated_weight || '',
      length: '', width: '', height: '', pieces: 1, declared_value: alert.declared_value || '',
      notes: alert.notes || '', location: 'miami_warehouse'
    });
    setReceivePhotos({ photo_package: null, photo_label: null, photo_package_preview: null, photo_label_preview: null });
    setShowReceiveModal(true);
  };

  const handleReceivePhotoChange = (photoType, file) => {
    if (file) {
      if (!file.type.startsWith('image/')) { alert('Solo se permiten archivos de imagen'); return; }
      if (file.size > 5 * 1024 * 1024) { alert('El archivo excede el tamano maximo de 5MB'); return; }
      const previewUrl = URL.createObjectURL(file);
      setReceivePhotos(prev => ({ ...prev, [`photo_${photoType}`]: file, [`photo_${photoType}_preview`]: previewUrl }));
    }
  };

  const removeReceivePhoto = (photoType) => {
    if (receivePhotos[`photo_${photoType}_preview`]) URL.revokeObjectURL(receivePhotos[`photo_${photoType}_preview`]);
    setReceivePhotos(prev => ({ ...prev, [`photo_${photoType}`]: null, [`photo_${photoType}_preview`]: null }));
  };

  const handleReceiveSubmit = async () => {
    if (!selectedAlert) return;
    setReceiveLoading(true);
    try {
      const response = await axios.post(`${API}/admin/alerts/${selectedAlert.id}/receive`, receiveForm, axiosConfig);
      const trackingId = response.data.tracking_id;

      if (receivePhotos.photo_package) {
        const formData = new FormData();
        formData.append('file', receivePhotos.photo_package);
        await axios.post(`${API}/admin/trackings/${trackingId}/upload-photo?photo_type=package`, formData,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      }
      if (receivePhotos.photo_label) {
        const formData = new FormData();
        formData.append('file', receivePhotos.photo_label);
        await axios.post(`${API}/admin/trackings/${trackingId}/upload-photo?photo_type=label`, formData,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      }

      setShowReceiveModal(false);
      setSelectedAlert(null);
      fetchAlerts();
      alert(`Paquete recibido exitosamente\n\nTracking: ${response.data.tracking_number}`);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setReceiveLoading(false);
    }
  };

  // Edit alert
  const openEditAlertModal = (a) => {
    setEditingAlert(a);
    setEditAlertForm({
      tracking_number: a.tracking_number || '', description: a.description || '',
      store_origin: a.store_origin || '', estimated_weight: a.estimated_weight || '',
      declared_value: a.declared_value || '', notes: a.notes || ''
    });
    setShowEditAlertModal(true);
  };

  const handleSaveEditAlert = async () => {
    if (!editingAlert) return;
    try {
      await axios.put(`${API}/admin/alerts/${editingAlert.id}`, editAlertForm, axiosConfig);
      setShowEditAlertModal(false);
      setEditingAlert(null);
      fetchAlerts();
      alert('Alerta actualizada exitosamente');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteAlert = async (alertId, trackingNumber) => {
    if (!window.confirm(`Estas seguro de eliminar la alerta del tracking ${trackingNumber}?\n\nEsta accion no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/alerts/${alertId}`, axiosConfig);
      fetchAlerts();
      alert('Alerta eliminada exitosamente');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleChangeAlertStatus = async (alertId, newStatus) => {
    if (!window.confirm(`Estas seguro de cambiar el estado a "${newStatus}"?`)) return;
    try {
      await axios.put(`${API}/admin/alerts/${alertId}/change-status?status=${newStatus}`, {}, axiosConfig);
      fetchAlerts();
      alert(`Estado cambiado exitosamente a: ${newStatus}`);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const pendingAlerts = alerts.filter(a => a.status === 'pending');
  const processedAlerts = alerts.filter(a => a.status !== 'pending');

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell size={24} />
            Alerta de Paquetes Entrantes
          </CardTitle>
          <CardDescription>Clientes notifican sobre paquetes que les van a llegar</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-900 font-semibold mb-1">Informacion:</p>
            <ul className="text-xs text-blue-800 space-y-1 ml-4">
              <li><strong>Procesar:</strong> Cuando el paquete llega fisicamente, haz clic para agregarlo automaticamente al sistema del cliente</li>
              <li className="text-orange-700"><strong>Cambiar estado:</strong> Usa el selector en el historial si procesaste una alerta por error</li>
            </ul>
          </div>

          {/* Sort & Group Controls */}
          <div className="flex gap-3 mb-4 items-center bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Ordenar por:</label>
              <select className="border rounded px-2 py-1 text-sm" value={alertSortBy} onChange={(e) => setAlertSortBy(e.target.value)}>
                <option value="date">Fecha</option>
                <option value="client">Cliente</option>
              </select>
            </div>
            <button onClick={() => setAlertSortOrder(alertSortOrder === 'asc' ? 'desc' : 'asc')} className="border rounded px-3 py-1 text-sm hover:bg-gray-200">
              {alertSortOrder === 'asc' ? '↑ A-Z' : '↓ Z-A'}
            </button>
            <div className="flex items-center gap-2 ml-auto">
              <input type="checkbox" id="groupByClient" checked={alertGroupByClient} onChange={(e) => setAlertGroupByClient(e.target.checked)} className="w-4 h-4" />
              <label htmlFor="groupByClient" className="text-sm font-medium cursor-pointer">Agrupar por cliente</label>
            </div>
          </div>

          <div className="space-y-3">
            {pendingAlerts.length === 0 && (
              <p className="text-center text-gray-500 py-8">No hay notificaciones pendientes</p>
            )}

            {!alertGroupByClient ? (
              getSortedAlerts(pendingAlerts).map(a => (
                <div key={a.id} className="border border-yellow-200 bg-yellow-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-lg">{a.tracking_number}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">{getClientName(a.client_id)}</span>
                      </p>
                      <p className="text-sm mt-2"><strong>Descripcion:</strong> {a.description}</p>
                      {a.store_origin && <p className="text-sm"><strong>Tienda:</strong> {a.store_origin}</p>}
                      {a.estimated_weight && <p className="text-sm"><strong>Peso estimado:</strong> {a.estimated_weight} lbs</p>}
                      {a.declared_value && <p className="text-sm"><strong>Valor declarado:</strong> ${a.declared_value}</p>}
                      {a.notes && <p className="text-sm text-gray-500 mt-1"><strong>Notas:</strong> {a.notes}</p>}
                      <p className="text-xs text-gray-400 mt-2">Creado: {new Date(a.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex flex-col gap-2 ml-4">
                      <Button size="sm" onClick={() => openReceiveModal(a)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`receive-alert-${a.id}`}>
                        Recibir Paquete
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEditAlertModal(a)} data-testid={`edit-alert-${a.id}`}>
                        Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteAlert(a.id, a.tracking_number)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-alert-${a.id}`}>
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              Object.entries(getGroupedAlerts(pendingAlerts))
                .sort((a, b) => alertSortOrder === 'asc' ? a[0].localeCompare(b[0]) : b[0].localeCompare(a[0]))
                .map(([clientName, clientAlerts]) => (
                  <div key={clientName} className="border-2 border-blue-300 rounded-lg p-4 bg-white">
                    <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm">{clientAlerts.length}</span>
                      {clientName}
                    </h3>
                    <div className="space-y-2 ml-4">
                      {clientAlerts.map(a => (
                        <div key={a.id} className="border border-yellow-200 bg-yellow-50 p-3 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-semibold">{a.tracking_number}</p>
                              <p className="text-sm mt-1"><strong>Descripcion:</strong> {a.description}</p>
                              {a.store_origin && <p className="text-xs text-gray-600"><strong>Tienda:</strong> {a.store_origin}</p>}
                              {a.estimated_weight && <p className="text-xs text-gray-600"><strong>Peso:</strong> {a.estimated_weight} lbs</p>}
                              {a.declared_value && <p className="text-xs text-gray-600"><strong>Valor:</strong> ${a.declared_value}</p>}
                              <p className="text-xs text-gray-400 mt-1">{new Date(a.created_at).toLocaleString()}</p>
                            </div>
                            <div className="flex flex-col gap-1 ml-3">
                              <Button size="sm" onClick={() => openReceiveModal(a)} className="bg-green-600 hover:bg-green-700 text-white text-xs">Recibir</Button>
                              <Button size="sm" variant="outline" onClick={() => openEditAlertModal(a)} className="text-xs">Editar</Button>
                              <Button size="sm" variant="outline" onClick={() => handleDeleteAlert(a.id, a.tracking_number)} className="text-red-600 hover:bg-red-50 text-xs">Eliminar</Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
            )}
          </div>

          {/* History */}
          <div className="mt-6">
            <h3 className="font-semibold mb-3">Historial de Alertas</h3>
            <div className="space-y-2">
              {processedAlerts.map(a => (
                <div key={a.id} className="border p-3 rounded-lg bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium">{a.tracking_number}</p>
                      <p className="text-sm text-gray-600">{getClientName(a.client_id)} - {a.description}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {a.status === 'processed' && a.processed_at && `Procesado: ${new Date(a.processed_at).toLocaleString()}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">Procesado</span>
                      <select className="text-xs border rounded px-2 py-1" value={a.status} onChange={(e) => handleChangeAlertStatus(a.id, e.target.value)}>
                        <option value="pending">Pendiente</option>
                        <option value="processed">Procesado</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Receive Modal */}
      {showReceiveModal && selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b bg-green-600 text-white rounded-t-lg">
              <h3 className="text-xl font-bold flex items-center gap-2">Recibir Paquete</h3>
              <p className="text-green-100 text-sm mt-1">Completa los datos del paquete alertado por el cliente</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-blue-800 font-semibold">Tracking</Label>
                    <p className="font-mono text-lg font-bold bg-white px-3 py-2 rounded border mt-1">{selectedAlert.tracking_number}</p>
                  </div>
                  <div>
                    <Label className="text-blue-800 font-semibold">Cliente</Label>
                    <p className="font-semibold bg-white px-3 py-2 rounded border mt-1">{getClientName(selectedAlert.client_id)}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-blue-800 font-semibold">Descripcion (de la alerta)</Label>
                  <p className="bg-white px-3 py-2 rounded border mt-1 text-sm">{selectedAlert.description || 'Sin descripcion'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nro. Paquete (Proveedor)</Label>
                  <Input value={receiveForm.package_number} onChange={(e) => setReceiveForm({...receiveForm, package_number: e.target.value})} placeholder="Numero del proveedor" data-testid="receive-package-number" />
                </div>
                <div>
                  <Label>Carrier</Label>
                  <Select value={receiveForm.carrier} onValueChange={(v) => setReceiveForm({...receiveForm, carrier: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Amazon">Amazon</SelectItem>
                      <SelectItem value="FedEx">FedEx</SelectItem>
                      <SelectItem value="UPS">UPS</SelectItem>
                      <SelectItem value="USPS">USPS</SelectItem>
                      <SelectItem value="DHL">DHL</SelectItem>
                      <SelectItem value="Otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div><Label>Peso (lb)</Label><Input type="number" step="0.1" value={receiveForm.weight} onChange={(e) => setReceiveForm({...receiveForm, weight: e.target.value})} data-testid="receive-weight" /></div>
                <div><Label>Largo (in)</Label><Input type="number" step="0.1" value={receiveForm.length} onChange={(e) => setReceiveForm({...receiveForm, length: e.target.value})} /></div>
                <div><Label>Ancho (in)</Label><Input type="number" step="0.1" value={receiveForm.width} onChange={(e) => setReceiveForm({...receiveForm, width: e.target.value})} /></div>
                <div><Label>Alto (in)</Label><Input type="number" step="0.1" value={receiveForm.height} onChange={(e) => setReceiveForm({...receiveForm, height: e.target.value})} /></div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div><Label>Piezas</Label><Input type="number" min="1" value={receiveForm.pieces} onChange={(e) => setReceiveForm({...receiveForm, pieces: e.target.value})} /></div>
                <div><Label>Valor Declarado ($)</Label><Input type="number" step="0.01" value={receiveForm.declared_value} onChange={(e) => setReceiveForm({...receiveForm, declared_value: e.target.value})} /></div>
                <div>
                  <Label>Ubicacion</Label>
                  <Select value={receiveForm.location} onValueChange={(v) => setReceiveForm({...receiveForm, location: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="miami_warehouse">Bodega Miami</SelectItem>
                      <SelectItem value="venezuela_warehouse">Bodega Venezuela</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div><Label>Notas adicionales</Label><Textarea value={receiveForm.notes} onChange={(e) => setReceiveForm({...receiveForm, notes: e.target.value})} placeholder="Observaciones del paquete..." rows={2} /></div>

              {/* Photos */}
              <div className="border-t pt-4">
                <Label className="text-lg font-semibold mb-3 block">Fotos del Paquete</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <p className="text-sm font-medium text-gray-600 mb-2">Foto del Paquete Cerrado</p>
                    {receivePhotos.photo_package_preview ? (
                      <div className="relative inline-block">
                        <img src={receivePhotos.photo_package_preview} alt="Paquete" className="max-h-32 rounded border mx-auto" />
                        <button type="button" onClick={() => removeReceivePhoto('package')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center">x</button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <div className="bg-gray-100 hover:bg-gray-200 rounded-lg py-8 px-4 transition-colors">
                          <Package className="mx-auto text-gray-400 mb-2" size={32} /><p className="text-sm text-gray-500">Clic para subir</p>
                        </div>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleReceivePhotoChange('package', e.target.files[0])} data-testid="receive-photo-package" />
                      </label>
                    )}
                  </div>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <p className="text-sm font-medium text-gray-600 mb-2">Foto de la Etiqueta</p>
                    {receivePhotos.photo_label_preview ? (
                      <div className="relative inline-block">
                        <img src={receivePhotos.photo_label_preview} alt="Etiqueta" className="max-h-32 rounded border mx-auto" />
                        <button type="button" onClick={() => removeReceivePhoto('label')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center">x</button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <div className="bg-gray-100 hover:bg-gray-200 rounded-lg py-8 px-4 transition-colors">
                          <FileText className="mx-auto text-gray-400 mb-2" size={32} /><p className="text-sm text-gray-500">Clic para subir</p>
                        </div>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleReceivePhotoChange('label', e.target.files[0])} data-testid="receive-photo-label" />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3 bg-gray-50 rounded-b-lg">
              <Button variant="outline" onClick={() => { setShowReceiveModal(false); setSelectedAlert(null); }} disabled={receiveLoading}>Cancelar</Button>
              <Button onClick={handleReceiveSubmit} disabled={receiveLoading} className="bg-green-600 hover:bg-green-700" data-testid="receive-submit-btn">
                {receiveLoading ? 'Guardando...' : 'Recibir y Crear Paquete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Alert Modal */}
      {showEditAlertModal && editingAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="p-4 border-b bg-blue-600 text-white rounded-t-lg">
              <h3 className="text-lg font-bold">Editar Alerta</h3>
            </div>
            <div className="p-6 space-y-4">
              <div><Label>Numero de Tracking</Label><Input value={editAlertForm.tracking_number} onChange={(e) => setEditAlertForm({...editAlertForm, tracking_number: e.target.value})} className="font-mono" /></div>
              <div><Label>Descripcion</Label><Textarea value={editAlertForm.description} onChange={(e) => setEditAlertForm({...editAlertForm, description: e.target.value})} rows={2} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Tienda/Origen</Label><Input value={editAlertForm.store_origin} onChange={(e) => setEditAlertForm({...editAlertForm, store_origin: e.target.value})} /></div>
                <div><Label>Peso Estimado (lbs)</Label><Input type="number" step="0.1" value={editAlertForm.estimated_weight} onChange={(e) => setEditAlertForm({...editAlertForm, estimated_weight: e.target.value})} /></div>
              </div>
              <div><Label>Valor Declarado ($)</Label><Input type="number" step="0.01" value={editAlertForm.declared_value} onChange={(e) => setEditAlertForm({...editAlertForm, declared_value: e.target.value})} /></div>
              <div><Label>Notas</Label><Textarea value={editAlertForm.notes} onChange={(e) => setEditAlertForm({...editAlertForm, notes: e.target.value})} rows={2} /></div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3 bg-gray-50 rounded-b-lg">
              <Button variant="outline" onClick={() => { setShowEditAlertModal(false); setEditingAlert(null); }}>Cancelar</Button>
              <Button onClick={handleSaveEditAlert} className="bg-blue-600 hover:bg-blue-700">Guardar Cambios</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAlerts;
